// src/components/UserMeet/User-Meet-Private/BreakoutStage.jsx
import React, { useCallback, useMemo, useState } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useParticipants,
  useLocalParticipant,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import {
  Users,
  UserPlus,
  ArrowLeft,
  StopCircle,
  Mic,
  MicOff,
  Video,
  VideoOff,
} from "lucide-react";
import { toast } from "sonner";

import LiveKitVideo from "../../../pages/MeetingRoom/ui/LiveKitVideo";
import BreakoutCountdownTimer from "./BreakoutCountdownTimer";
import LeaveBreakoutModal from "./LeaveBreakoutModal";

/* ─── Single Participant Tile ─── */
function ParticipantTile({ trackRef, participant, isLocal }) {
  const name = participant?.name || participant?.identity || "Unknown";
  const isMuted = !participant?.isMicrophoneEnabled;
  const isCamOff = !trackRef?.publication?.track;

  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className={`relative w-full h-full rounded-2xl overflow-hidden border transition-all ${
        participant?.isSpeaking
          ? "border-green-400/60 shadow-lg shadow-green-500/20"
          : "border-white/10"
      } bg-gradient-to-br from-slate-800 to-slate-900`}
    >
      {!isCamOff && trackRef ? (
        <LiveKitVideo
          trackRef={trackRef}
          muted={isLocal}
          objectFit="cover"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
            {initials}
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {participant?.isSpeaking && (
              <div className="flex items-center gap-0.5">
                <span className="w-1 h-3 bg-green-400 rounded-full animate-pulse" />
                <span
                  className="w-1 h-4 bg-green-400 rounded-full animate-pulse"
                  style={{ animationDelay: "0.15s" }}
                />
                <span
                  className="w-1 h-2.5 bg-green-400 rounded-full animate-pulse"
                  style={{ animationDelay: "0.3s" }}
                />
              </div>
            )}
            <span className="text-sm font-semibold text-white truncate">
              {isLocal ? `${name} (You)` : name}
            </span>
          </div>

          <div
            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
              isMuted
                ? "bg-red-500/20 border border-red-500/40"
                : "bg-white/10 border border-white/20"
            }`}
          >
            {isMuted ? (
              <MicOff size={12} className="text-red-400" />
            ) : (
              <Mic size={12} className="text-white/80" />
            )}
          </div>
        </div>
      </div>

      {isLocal && (
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-purple-500/80 backdrop-blur-sm rounded-md text-[10px] font-bold text-white uppercase tracking-wide">
          You
        </div>
      )}
    </div>
  );
}

/* ─── Participant Grid ─── */
function ParticipantGrid({ tracks, localParticipant, remoteParticipants }) {
  const cameraTrackMap = useMemo(() => {
    const map = {};
    (tracks || []).forEach((t) => {
      if (t.source === Track.Source.Camera && t.participant) {
        map[t.participant.identity] = t;
      }
    });
    return map;
  }, [tracks]);

  const allParticipants = useMemo(() => {
    const list = [];

    if (localParticipant) {
      list.push({
        participant: localParticipant,
        trackRef: cameraTrackMap[localParticipant.identity] || null,
        isLocal: true,
      });
    }

    (remoteParticipants || []).forEach((p) => {
      if (p.identity === localParticipant?.identity) return;
      list.push({
        participant: p,
        trackRef: cameraTrackMap[p.identity] || null,
        isLocal: false,
      });
    });

    return list;
  }, [localParticipant, remoteParticipants, cameraTrackMap]);

  const count = allParticipants.length;

  const gridClass = useMemo(() => {
    if (count === 1) return "grid-cols-1 grid-rows-1";
    if (count === 2) return "grid-cols-2 grid-rows-1";
    if (count <= 4) return "grid-cols-2 grid-rows-2";
    if (count <= 6) return "grid-cols-3 grid-rows-2";
    return "grid-cols-3 grid-rows-3";
  }, [count]);

  if (count === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <Users size={48} className="text-white/20 mx-auto mb-4" />
          <p className="text-lg font-semibold text-white/60">
            Waiting for participants...
          </p>
          <p className="text-sm text-white/40 mt-1">
            Invite others to join this breakout room
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full grid ${gridClass} gap-3 p-4`}>
      {allParticipants.map(({ participant, trackRef, isLocal }) => (
        <ParticipantTile
          key={participant.identity}
          participant={participant}
          trackRef={trackRef}
          isLocal={isLocal}
        />
      ))}
    </div>
  );
}

/* ─── Inner component — lives INSIDE LiveKitRoom context ─── */
function BreakoutRoomInner({
  breakoutData,
  onLeave,
  onEnd,
  onInvite,
  isOwner,
  onExpire,
}) {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useParticipants();

  // ✅ Leave confirmation modal state
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const totalParticipants = useMemo(() => {
    let count = localParticipant ? 1 : 0;
    count += (remoteParticipants || []).filter(
      (p) => p.identity !== localParticipant?.identity,
    ).length;
    return count;
  }, [localParticipant, remoteParticipants]);

  const micOn = localParticipant?.isMicrophoneEnabled ?? false;
  const camOn = localParticipant?.isCameraEnabled ?? false;

  const toggleMic = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setMicrophoneEnabled(!micOn);
    } catch (err) {
      toast.error("Failed to toggle microphone");
    }
  }, [localParticipant, micOn]);

  const toggleCam = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setCameraEnabled(!camOn);
    } catch (err) {
      toast.error("Failed to toggle camera");
    }
  }, [localParticipant, camOn]);

  // ✅ Handle confirmed leave
  const handleConfirmLeave = useCallback(async () => {
    setIsLeaving(true);
    setShowLeaveModal(false);
    onLeave?.();
    // setIsLeaving will be cleaned up when component unmounts
  }, [onLeave]);

  return (
    <>
      <div className="w-full h-full flex flex-col bg-gradient-to-br from-slate-950 to-slate-900 rounded-3xl overflow-hidden">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3 bg-black/30 backdrop-blur-xl border-b border-white/10 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-br from-purple-500/15 to-violet-500/15 border border-purple-500/30 rounded-lg text-xs font-bold text-purple-300 uppercase tracking-wide">
              <Users size={12} />
              <span>Breakout</span>
            </div>
            <div className="px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/70 font-semibold">
              {totalParticipants} participant
              {totalParticipants !== 1 ? "s" : ""}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {breakoutData.expiresAt && (
              <BreakoutCountdownTimer
                expiresAt={breakoutData.expiresAt}
                onExpire={onExpire}
              />
            )}
          </div>
        </div>

        {/* ── Participant Grid ── */}
        <div className="flex-1 overflow-hidden">
          <ParticipantGrid
            tracks={tracks}
            localParticipant={localParticipant}
            remoteParticipants={remoteParticipants}
          />
        </div>

        {/* ── Controls ── */}
        <div className="flex items-center justify-center gap-2.5 px-5 py-3 bg-black/30 backdrop-blur-xl border-t border-white/10 flex-wrap">
          {/* Mic */}
          <button
            onClick={toggleMic}
            type="button"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:-translate-y-0.5 ${
              micOn
                ? "bg-white/10 border-white/20 text-white hover:bg-white/15"
                : "bg-red-500/15 border-red-500/30 text-red-400 hover:bg-red-500/25"
            }`}
          >
            {micOn ? <Mic size={16} /> : <MicOff size={16} />}
            <span className="hidden sm:inline">
              {micOn ? "Mute" : "Unmute"}
            </span>
          </button>

          {/* Camera */}
          <button
            onClick={toggleCam}
            type="button"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:-translate-y-0.5 ${
              camOn
                ? "bg-white/10 border-white/20 text-white hover:bg-white/15"
                : "bg-red-500/15 border-red-500/30 text-red-400 hover:bg-red-500/25"
            }`}
          >
            {camOn ? <Video size={16} /> : <VideoOff size={16} />}
            <span className="hidden sm:inline">
              {camOn ? "Camera" : "No Cam"}
            </span>
          </button>

          <div className="w-px h-8 bg-white/10 mx-1 hidden sm:block" />

          {/* Invite (owner only) */}
          {isOwner && onInvite && (
            <button
              onClick={onInvite}
              type="button"
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-blue-500/15 to-indigo-500/15 border border-blue-500/30 text-blue-400 rounded-xl text-sm font-semibold hover:from-blue-500/25 hover:to-indigo-500/25 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-0.5 transition-all"
            >
              <UserPlus size={16} />
              <span className="hidden sm:inline">Invite</span>
            </button>
          )}

          {/* End (owner only) */}
          {isOwner && onEnd && (
            <button
              onClick={onEnd}
              type="button"
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-red-500/15 to-rose-500/15 border border-red-500/30 text-red-400 rounded-xl text-sm font-semibold hover:from-red-500/25 hover:to-rose-500/25 hover:border-red-500/50 hover:shadow-lg hover:shadow-red-500/20 hover:-translate-y-0.5 transition-all"
            >
              <StopCircle size={16} />
              <span className="hidden sm:inline">End</span>
            </button>
          )}

          <div className="w-px h-8 bg-white/10 mx-1 hidden sm:block" />

          {/* ✅ Return to Main — now opens confirmation modal */}
          <button
            onClick={() => setShowLeaveModal(true)}
            type="button"
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-white/10 to-white/5 border border-white/20 text-white/90 rounded-xl text-sm font-semibold hover:from-white/15 hover:to-white/10 hover:border-white/30 hover:shadow-lg hover:shadow-white/10 hover:-translate-y-0.5 transition-all"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Return to Main</span>
          </button>
        </div>
      </div>

      {/* ✅ Leave Confirmation Modal */}
      <LeaveBreakoutModal
        open={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirmLeave={handleConfirmLeave}
        leaving={isLeaving}
        isOwner={isOwner}
      />
    </>
  );
}

/* ─── Outer wrapper — creates the LiveKitRoom context ─── */
export default function BreakoutStage({
  breakoutData,
  deviceStatus,
  setDeviceStatus,
  onLeave,
  onEnd,
  onInvite,
  isOwner,
  onExpire,
}) {
  const serverUrl = useMemo(() => {
    if (!breakoutData?.livekitServerUrl) return "";
    let url = breakoutData.livekitServerUrl;
    const isHttps = window.location.protocol === "https:";

    if (isHttps) {
      if (url.startsWith("ws://")) url = url.replace(/^ws:\/\//, "wss://");
      else if (url.startsWith("http://"))
        url = url.replace(/^http:\/\//, "https://");
    }
    return url;
  }, [breakoutData?.livekitServerUrl]);

  const handleRoomConnected = useCallback(() => {
    console.log("✅ Connected to breakout LiveKit room");
    toast.success("Connected to breakout room");
  }, []);

  const handleRoomDisconnected = useCallback(() => {
    console.log("🔌 Disconnected from breakout LiveKit room");
  }, []);

  const handleRoomError = useCallback((err) => {
    if (err?.message?.includes("Client initiated disconnect")) return;
    console.error("❌ Breakout LiveKit room error:", err);
    toast.error("Breakout room connection error. Please try again.");
  }, []);

  if (!breakoutData || !serverUrl || !breakoutData.livekitToken) {
    return null;
  }

  const audio = deviceStatus?.audioAllowed ?? true;
  const video = deviceStatus?.videoAllowed ?? true;

  return (
    <LiveKitRoom
      key={`breakout-${breakoutData.breakoutId}`}
      serverUrl={serverUrl}
      token={breakoutData.livekitToken}
      connect={true}
      audio={audio}
      video={video}
      data-lk-theme="default"
      options={{ adaptiveStream: true, dynacast: true }}
      onConnected={handleRoomConnected}
      onDisconnected={handleRoomDisconnected}
      onError={handleRoomError}
      className="w-full h-full"
    >
      <RoomAudioRenderer />

      <BreakoutRoomInner
        breakoutData={breakoutData}
        onLeave={onLeave}
        onEnd={onEnd}
        onInvite={onInvite}
        isOwner={isOwner}
        onExpire={onExpire}
      />
    </LiveKitRoom>
  );
}